import { useEffect } from "react";
import { Modal, Form, Alert, ListGroup } from "react-bootstrap";
import axios from "axios";
import Button from "components/base/Button";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";

interface ChangePasswordModalProps {
  show: boolean;
  onHide: () => void;
}

interface ChangePasswordFormInputs {
  senhaAtual: string;
  novaSenha: string;
  confirmaSenha: string;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  show,
  onHide,
}) => {
  // React Hook Form setup
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, touchedFields, isSubmitting },
    setError,
  } = useForm<ChangePasswordFormInputs>({
    defaultValues: {
      senhaAtual: "",
      novaSenha: "",
      confirmaSenha: "",
    },
    mode: "onTouched",
  });

  // Watch password field for validation
  const novaSenha = watch("novaSenha");

  // React Query mutation setup
  const changePasswordMutation = useMutation({
    mutationFn: async (data: ChangePasswordFormInputs) => {
      const response = await axios.post("/api/Account/change-password", data);
      return response.data;
    },
    onSuccess: () => {
      // Reset form on success
      reset();

      // Close modal after delay
      setTimeout(() => {
        onHide();
      }, 2000);
    },
    onError: (error: any) => {
      // Handle API errors and map them to form fields
      if (error.response?.data) {
        if (
          typeof error.response.data === "object" &&
          error.response.data !== null
        ) {
          // Handle structured validation errors
          Object.entries(error.response.data).forEach(([key, value]) => {
            const fieldName = key.charAt(0).toLowerCase() + key.slice(1);
            const errorMsg = Array.isArray(value)
              ? (value as string[]).join(". ")
              : String(value);

            if (fieldName === "" || fieldName === "undefined") {
              // General error not tied to a specific field
              setError("root.serverError", {
                type: "server",
                message: errorMsg,
              });
            } else {
              // Field-specific error
              setError(fieldName as keyof ChangePasswordFormInputs, {
                type: "server",
                message: errorMsg,
              });
            }
          });
        } else if (typeof error.response.data === "string") {
          setError("root.serverError", {
            type: "server",
            message: error.response.data,
          });
        }
      } else {
        setError("root.serverError", {
          type: "server",
          message: "Ocorreu um erro ao alterar a senha. Tente novamente.",
        });
      }
    },
  });

  // Reset form when modal is closed
  useEffect(() => {
    if (!show) {
      reset();
      changePasswordMutation.reset();
    }
  }, [show, reset, changePasswordMutation]);

  // Form submission handler
  const onSubmit = (data: ChangePasswordFormInputs) => {
    changePasswordMutation.mutate(data);
  };

  // Error handling helpers
  const serverError = errors.root?.serverError?.message;
  const isPasswordMismatch =
    serverError?.includes("não coincidem") ||
    errors.confirmaSenha?.type === "validate";
  const isComplexityError =
    serverError?.includes("caractere não alfanumérico") ||
    serverError?.includes("letra maiúscula") ||
    serverError?.includes("mínimo");

  // Dividir múltiplas mensagens de erro para melhor exibição
  const errorMessages = serverError?.split(". ").filter(Boolean) || [];

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Alterar Senha</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Alerta para erros de complexidade */}
        {serverError && isComplexityError && (
          <Alert variant="subtle-warning" className="mt-3 mb-3 text-start">
            <strong>Requisitos de senha:</strong>
            <ListGroup variant="flush" className="mt-2 border-0">
              {errorMessages.map((error, index) => (
                <ListGroup.Item
                  key={index}
                  className="border-0 bg-transparent ps-2 py-1"
                >
                  • {error}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Alert>
        )}

        {/* Alerta para outros erros que não são de complexidade ou não-coincidência */}
        {serverError && !isComplexityError && !isPasswordMismatch && (
          <Alert variant="subtle-danger" className="mt-3 mb-3 text-start">
            <strong>Erro: </strong>
            {serverError}
          </Alert>
        )}

        {/* Alerta para sucesso */}
        {changePasswordMutation.isSuccess && (
          <Alert variant="subtle-success" className="mt-3 mb-3 text-start">
            {changePasswordMutation.data?.message ||
              "Senha alterada com sucesso!"}
          </Alert>
        )}

        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Group className="mb-3">
            <Form.Label>Senha Atual</Form.Label>
            <Form.Control
              type="password"
              placeholder="Digite sua senha atual"
              autoComplete="current-password"
              isInvalid={!!errors.senhaAtual}
              {...register("senhaAtual", {
                required: "A senha atual é obrigatória",
              })}
            />
            <Form.Control.Feedback type="invalid">
              {errors.senhaAtual?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Nova Senha</Form.Label>
            <Form.Control
              type="password"
              placeholder="Digite a nova senha"
              autoComplete="new-password"
              isInvalid={!!errors.novaSenha}
              {...register("novaSenha", {
                required: "A nova senha é obrigatória",
                minLength: {
                  value: 8,
                  message: "A nova senha deve ter pelo menos 8 caracteres",
                },
              })}
            />
            <Form.Control.Feedback type="invalid">
              {errors.novaSenha?.message}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              A senha deve ter pelo menos 8 caracteres.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Confirme a Nova Senha</Form.Label>
            <Form.Control
              type="password"
              placeholder="Confirme a nova senha"
              autoComplete="new-password"
              isInvalid={!!errors.confirmaSenha}
              {...register("confirmaSenha", {
                required: "A confirmação de senha é obrigatória",
                validate: (value) =>
                  value === novaSenha || "As senhas não coincidem",
              })}
            />
            <Form.Control.Feedback type="invalid">
              {errors.confirmaSenha?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="d-grid gap-2 mt-4">
            <Button
              variant="primary"
              type="submit"
              loading={changePasswordMutation.isPending || isSubmitting}
              disabled={changePasswordMutation.isPending || isSubmitting}
              loadingPosition="end"
              className="w-100"
            >
              {changePasswordMutation.isPending || isSubmitting
                ? "Processando..."
                : "Alterar Senha"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ChangePasswordModal;
